import React, { useEffect, useState } from 'react';
import './RequestForm.css';
import DoodleStep from './steps/DoodleStep';
import ContactStep from './steps/ContactStep';
import ConfirmationStep from './steps/ConfirmationStep';

export interface ArtRequest {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  artDescription: string;
  deliveryMethod: 'email' | 'mail';
  isOver18: boolean;
}

interface RequestFormProps {
  onBackToHome: () => void;
}

const DRAFT_STORAGE_KEY = 'doodle-delivery-draft-v1';
const MAX_RESUMABLE_STEP = 2;

const emptyFormData: ArtRequest = {
  firstName: '',
  lastName: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  artDescription: '',
  deliveryMethod: 'email',
  isOver18: false,
};

type Draft = { step: number; data: ArtRequest };

const loadDraft = (): Draft | null => {
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Draft>;
    if (!parsed || typeof parsed !== 'object' || !parsed.data) return null;
    return {
      step: Math.min(Math.max(Number(parsed.step) || 1, 1), MAX_RESUMABLE_STEP),
      data: { ...emptyFormData, ...parsed.data },
    };
  } catch {
    return null;
  }
};

const clearDraft = () => {
  try {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore — private mode / quota
  }
};

const RequestForm: React.FC<RequestFormProps> = ({ onBackToHome }) => {
  const [currentStep, setCurrentStep] = useState(() => loadDraft()?.step ?? 1);
  const [formData, setFormData] = useState<ArtRequest>(() => loadDraft()?.data ?? emptyFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const totalSteps = 3;

  useEffect(() => {
    if (currentStep >= 3) return;
    try {
      window.localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({ step: currentStep, data: formData }),
      );
    } catch {
      // ignore — private mode / quota
    }
  }, [currentStep, formData]);

  const updateFormData = (data: Partial<ArtRequest>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const isDev = process.env.NODE_ENV === 'development';

    if (isDev) {
      clearDraft();
      setCurrentStep(3);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_APIG_ENDPOINT;
      const response = await fetch(apiUrl ?? '', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        mode: 'cors',
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(errorText || `request failed: ${response.status}`);
      }

      clearDraft();
      setCurrentStep(3);
    } catch (error) {
      console.error('Art request submission failed:', error);
      setSubmitError("sorry, we couldn't submit your request right now. please try again in a moment.");
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DoodleStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={nextStep}
            onBack={onBackToHome}
          />
        );
      case 2:
        return (
          <ContactStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleSubmit}
            onBack={prevStep}
            submitError={submitError}
          />
        );
      case 3:
        return (
          <ConfirmationStep
            formData={formData}
            onBackToHome={onBackToHome}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="request-form-container">
      <div className="form-header">
        <button className="back-button" onClick={onBackToHome}>
          ← back to home
        </button>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>
        <div className="step-indicator">
          step {currentStep} of {totalSteps}
        </div>
      </div>

      <div className="form-content">
        {renderStep()}
      </div>
    </div>
  );
};

export default RequestForm;
